import React, { useEffect, useRef, useId, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { loadGoogleMapsPlaces } from '../utils/loadGoogleMapsPlaces';
import { locationsAPI } from '../api';

interface LocationAutocompleteProps {
  onPlaceSelect: (place: {
    address: string;
    city: string;
    state: string;
    stateAbbreviation: string;
    postalCode?: string;
  }) => void;
  onInputChange?: (value: string) => void;
  value?: string;
  placeholder?: string;
  className?: string;
  /** Stable id for labels / uniqueness when multiple fields exist on one page */
  inputId?: string;
}

declare global {
  interface Window {
    google: any;
  }
}

/** Parse formatted_address when address_components are missing (Places widget edge cases). */
function parseFromFormattedAddress(formatted: string): {
  address: string;
  city: string;
  state: string;
  stateAbbreviation: string;
  postalCode: string;
} | null {
  const trimmed = formatted?.trim();
  if (!trimmed) return null;
  const withoutCountry = trimmed
    .replace(/\s*,\s*(United States of America|United States|USA|U\.S\.A\.|U\.S\.)\s*$/i, '')
    .trim();
  const parts = withoutCountry.split(',').map((s) => s.trim()).filter(Boolean);
  let city = '';
  let stateAbbr = '';
  let postalCode = '';
  const zipGlob = withoutCountry.match(/\b(\d{5}(-\d{4})?)\b/);
  if (zipGlob) postalCode = zipGlob[1];
  if (parts.length >= 2) city = parts[1];
  if (parts.length >= 3) {
    const stateZip = parts[parts.length - 1];
    const sm = stateZip?.match(/^([A-Za-z]{2})\s+(\d{5})/);
    if (sm) {
      stateAbbr = sm[1].toUpperCase();
      if (!postalCode) postalCode = sm[2];
    } else {
      const z = stateZip?.match(/(\d{5}(-\d{4})?)/);
      if (z && !postalCode) postalCode = z[1];
      stateAbbr = stateZip?.replace(/\s*\d{5}(-\d{4})?.*$/, '').trim() || '';
    }
  }
  return {
    address: withoutCountry,
    city,
    state: '',
    stateAbbreviation: stateAbbr,
    postalCode,
  };
}

function buildPlacePayload(place: any): {
  address: string;
  city: string;
  state: string;
  stateAbbreviation: string;
  postalCode?: string;
} | null {
  const address = place.formatted_address || place.name || '';
  if (!address) return null;

  if (!place.address_components?.length) {
    return parseFromFormattedAddress(address);
  }

  let city = '';
  let state = '';
  let stateAbbreviation = '';
  let postalCode = '';

  place.address_components.forEach((component: any) => {
    const types: string[] = component.types || [];
    if (types.includes('locality')) city = component.long_name;
    else if (types.includes('postal_town') && !city) city = component.long_name;
    else if ((types.includes('sublocality') || types.includes('neighborhood')) && !city) {
      city = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      state = component.long_name;
      stateAbbreviation = component.short_name;
    } else if (types.includes('postal_code')) postalCode = component.long_name;
    else if (types.includes('postal_code_suffix') && !postalCode) {
      const basePostal = place.address_components.find((c: any) => c.types.includes('postal_code'));
      if (basePostal) postalCode = `${basePostal.long_name}-${component.long_name}`;
    }
  });

  if (!postalCode && address) {
    const zipMatch = address.match(/\b(\d{5}(-\d{4})?)\b/);
    if (zipMatch) postalCode = zipMatch[1];
  }

  const payload = {
    address,
    city: city || '',
    state: state || '',
    stateAbbreviation: stateAbbreviation || '',
    postalCode: postalCode || '',
  };
  if (!payload.city && !payload.stateAbbreviation && address.includes(',')) {
    const fallback = parseFromFormattedAddress(address);
    if (fallback) {
      return {
        address: fallback.address,
        city: fallback.city || payload.city,
        state: fallback.state,
        stateAbbreviation: fallback.stateAbbreviation || payload.stateAbbreviation,
        postalCode: fallback.postalCode || payload.postalCode,
      };
    }
  }
  return payload;
}

type ServerSuggestion = {
  address: string;
  city: string;
  state: string;
  stateAbbreviation: string;
  postalCode: string;
};

/** Production: Mapbox/Nominatim via API — no Google Maps JS (avoids referrer/key overlay errors). */
const ServerLocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  onPlaceSelect,
  onInputChange,
  value = '',
  placeholder = 'Enter location (address, city, state)',
  className = '',
  inputId: inputIdProp,
}) => {
  const reactId = useId();
  const inputId = inputIdProp ?? `loc-ac-${reactId.replace(/:/g, '')}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = `${inputId}-list`;
  const debounceRef = useRef<number | undefined>(undefined);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const onInputChangeRef = useRef(onInputChange);
  onPlaceSelectRef.current = onPlaceSelect;
  onInputChangeRef.current = onInputChange;

  const [suggestions, setSuggestions] = useState<ServerSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listRect, setListRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const updateListRect = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vv = window.visualViewport;
    const viewTop = vv?.offsetTop ?? 0;
    const viewHeight = vv?.height ?? window.innerHeight;
    const gap = 4;
    const listMax = 240;
    const minTop = viewTop + 8;
    const maxTop = viewTop + viewHeight - listMax - 8;
    let top = r.bottom + gap;
    if (top > maxTop) {
      const above = r.top - gap - listMax;
      top = above >= minTop ? above : Math.max(minTop, Math.min(top, maxTop));
    }
    top = Math.max(minTop, top);
    setListRect({ top, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.value !== value) {
      el.value = value;
    }
  }, [value]);

  const runSuggest = useCallback((text: string) => {
    const q = text.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    locationsAPI
      .addressSuggest(q)
      .then((data) => {
        const list = data.suggestions || [];
        setSuggestions(list);
        const show = list.length > 0;
        setOpen(show);
        if (show) queueMicrotask(() => updateListRect());
      })
      .catch(() => {
        setSuggestions([]);
        setOpen(false);
      })
      .finally(() => setLoading(false));
  }, [updateListRect]);

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      onInputChangeRef.current?.(v);
      if (debounceRef.current !== undefined) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => runSuggest(v), 350);
    },
    [runSuggest]
  );

  const pick = useCallback((s: ServerSuggestion) => {
    onPlaceSelectRef.current({
      address: s.address,
      city: s.city || '',
      state: s.state || '',
      stateAbbreviation: s.stateAbbreviation || '',
      postalCode: s.postalCode || '',
    });
    onInputChangeRef.current?.(s.address);
    if (inputRef.current) inputRef.current.value = s.address;
    setSuggestions([]);
    setOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current !== undefined) window.clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open || suggestions.length === 0) {
      setListRect(null);
      return;
    }
    updateListRect();
    const onMove = () => updateListRect();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    window.visualViewport?.addEventListener('scroll', onMove);
    window.visualViewport?.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      window.visualViewport?.removeEventListener('scroll', onMove);
      window.visualViewport?.removeEventListener('resize', onMove);
    };
  }, [open, suggestions, updateListRect]);

  const listEl =
    open && suggestions.length > 0 && listRect
      ? createPortal(
          <ul
            id={listId}
            role="listbox"
            style={{
              position: 'fixed',
              top: listRect.top,
              left: listRect.left,
              width: listRect.width,
              zIndex: 200000,
            }}
            className="max-h-60 overflow-auto rounded-md border border-gray-200 bg-white py-1 text-left shadow-lg dark:border-zinc-600 dark:bg-zinc-900"
          >
            {suggestions.map((s, i) => (
              <li key={`${s.address}-${i}`} role="option">
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-zinc-800"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(s)}
                >
                  <span className="block text-gray-900 dark:text-zinc-100">{s.address}</span>
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        id={inputId}
        name="street-address"
        autoComplete="street-address"
        enterKeyHint="next"
        defaultValue={value}
        onChange={handleInput}
        onFocus={() => {
          if (suggestions.length > 0) {
            setOpen(true);
            queueMicrotask(() => updateListRect());
          }
        }}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 150);
        }}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        role="combobox"
        placeholder={placeholder}
        className={className}
      />
      {listEl}
      {loading && (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
          …
        </span>
      )}
    </div>
  );
};

const GoogleLocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  onPlaceSelect,
  onInputChange,
  value = '',
  placeholder = 'Enter location (address, city, state)',
  className = '',
  inputId: inputIdProp,
}) => {
  const reactId = useId();
  const inputId = inputIdProp ?? `loc-ac-${reactId.replace(/:/g, '')}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const autocompleteInitializedRef = useRef(false);
  const observerRef = useRef<MutationObserver | null>(null);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const onInputChangeRef = useRef(onInputChange);
  onPlaceSelectRef.current = onPlaceSelect;
  onInputChangeRef.current = onInputChange;

  // Google Places mutates the input DOM; a fully controlled value fights the widget and clears suggestions.
  const handleInput = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onInputChangeRef.current?.(e.target.value);
  }, []);

  // Sync parent value → input when not focused (initial load, clear form, programmatic updates).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.value !== value) {
      el.value = value;
    }
  }, [value]);

  const noAutocomplete = 'nope';
  const protectAutocompleteAttr = React.useCallback(() => {
    if (!inputRef.current) return;

    inputRef.current.setAttribute('autocomplete', noAutocomplete);

    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'autocomplete') {
          const el = m.target as HTMLInputElement;
          if (el.getAttribute('autocomplete') !== noAutocomplete) {
            el.setAttribute('autocomplete', noAutocomplete);
          }
        }
      }
    });
    observerRef.current.observe(inputRef.current, { attributes: true, attributeFilter: ['autocomplete'] });
  }, []);

  const emitPlace = React.useCallback((place: any) => {
    const payload = buildPlacePayload(place);
    if (!payload) return;
    onPlaceSelectRef.current(payload);
    onInputChangeRef.current?.(payload.address);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initializeAutocomplete = () => {
      if (cancelled || !inputRef.current || !window.google?.maps?.places || autocompleteInitializedRef.current) {
        return;
      }

      try {
        const opts: Record<string, unknown> = {
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
        };
        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, opts);
        autocompleteInitializedRef.current = true;

        protectAutocompleteAttr();

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place) return;

          if (!place.address_components?.length && place.place_id && window.google.maps.places.PlacesService) {
            const svc = new window.google.maps.places.PlacesService(document.createElement('div'));
            svc.getDetails(
              {
                placeId: place.place_id,
                fields: ['address_components', 'formatted_address', 'name', 'place_id'],
              },
              (detailed: any, status: string) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && detailed) {
                  emitPlace(detailed);
                } else {
                  emitPlace(place);
                }
              }
            );
            return;
          }

          emitPlace(place);
        });

        autocompleteRef.current = autocomplete;
      } catch (err) {
        console.warn(
          'Google Places Autocomplete failed. Enable Maps JavaScript API + Places API, billing, and HTTP referrer (localhost + 127.0.0.1 for dev).',
          err
        );
      }
    };

    loadGoogleMapsPlaces()
      .then(() => {
        if (cancelled) return;
        queueMicrotask(() => initializeAutocomplete());
      })
      .catch((err) => {
        console.warn('[LocationAutocomplete]', err instanceof Error ? err.message : err);
      });

    return () => {
      cancelled = true;
      autocompleteInitializedRef.current = false;
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [protectAutocompleteAttr, emitPlace]);

  return (
    <input
      ref={inputRef}
      type="text"
      id={inputId}
      name="street-address"
      autoComplete="nope"
      enterKeyHint="next"
      defaultValue={value}
      onChange={handleInput}
      placeholder={placeholder}
      className={className}
    />
  );
};

/**
 * Default: server-backed suggestions (GET /api/locations/address-suggest) — no Google Maps JS, no referrer/key overlay.
 * Opt-in Google Places (local testing): set REACT_APP_USE_GOOGLE_PLACES=true before build/start.
 */
const LocationAutocomplete: React.FC<LocationAutocompleteProps> = (props) => {
  const useGooglePlaces = process.env.REACT_APP_USE_GOOGLE_PLACES === 'true';
  if (useGooglePlaces) {
    return <GoogleLocationAutocomplete {...props} />;
  }
  return <ServerLocationAutocomplete {...props} />;
};

export default LocationAutocomplete;
